import React, { useCallback, useEffect, useState, useRef, createContext, useContext } from 'react';
import { supabase } from './supabase';
interface KitchenPauseContextType {
  /** Whether the current restaurant's kitchen is paused */
  isKitchenPaused: boolean;
  /** The restaurant ID being monitored */
  monitoredRestaurantId: string | null;
  /** Call this from any page that knows the restaurant ID to start monitoring */
  monitorRestaurant: (restaurantId: string) => void;
}
const KitchenPauseContext = createContext<KitchenPauseContextType>({
  isKitchenPaused: false,
  monitoredRestaurantId: null,
  monitorRestaurant: () => {}
});
const LOG_PREFIX = '[NOIZA][KitchenPause]';
export function KitchenPauseProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isKitchenPaused, setIsKitchenPaused] = useState(false);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const channelRef = useRef<any>(null);
  const intervalRef = useRef<any>(null);
  const monitorRestaurant = useCallback((id: string) => {
    if (!id) return;
    setRestaurantId(prev => {
      if (prev === id) return prev;
      console.log(LOG_PREFIX, 'Now monitoring restaurant:', id);
      return id;
    });
  }, []);
  // Fetch kitchen_paused from DB
  const fetchPausedState = useCallback(async (rid: string) => {
    try {
      const {
        data,
        error
      } = await supabase.from('restaurant_settings').select('kitchen_paused').eq('restaurant_id', rid).single();
      if (error) {
        console.warn(LOG_PREFIX, 'Fetch error:', error.message);
        return;
      }
      const paused = data?.kitchen_paused === true;
      setIsKitchenPaused(prev => {
        if (prev !== paused) {
          console.log(LOG_PREFIX, `State changed: ${prev} → ${paused} (restaurant: ${rid})`);
        }
        return paused;
      });
    } catch (err) {
      console.warn(LOG_PREFIX, 'Fetch exception:', err);
    }
  }, []);
  // Setup polling + realtime subscription whenever restaurantId changes
  useEffect(() => {
    // Cleanup previous
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (!restaurantId) {
      setIsKitchenPaused(false);
      return;
    }
    console.log(LOG_PREFIX, 'Setting up monitoring for:', restaurantId);
    // Immediate fetch
    fetchPausedState(restaurantId);
    // Poll every 30 seconds (realtime subscription handles instant updates)
    intervalRef.current = setInterval(() => {
      fetchPausedState(restaurantId);
    }, 30000);
    // Realtime subscription for instant updates
    const channel = supabase.channel(`kitchen-pause-${restaurantId}`).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'restaurant_settings',
      filter: `restaurant_id=eq.${restaurantId}`
    }, (payload: any) => {
      const paused = payload.new?.kitchen_paused === true;
      console.log(LOG_PREFIX, 'Realtime update received:', {
        paused,
        restaurantId
      });
      setIsKitchenPaused(paused);
    }).subscribe((status: string) => {
      console.log(LOG_PREFIX, 'Realtime subscription status:', status);
    });
    channelRef.current = channel;
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (channelRef.current) supabase.removeChannel(channelRef.current);
    };
  }, [restaurantId, fetchPausedState]);
  return <KitchenPauseContext.Provider value={{
    isKitchenPaused,
    monitoredRestaurantId: restaurantId,
    monitorRestaurant
  }} data-id="element-2951">
      {children}
    </KitchenPauseContext.Provider>;
}
export function useKitchenPause() {
  return useContext(KitchenPauseContext);
}
/**
 * Backend guard: re-checks kitchen_paused directly from DB before order creation.
 * Call this right before inserting an order.
 */
export async function checkKitchenPausedBeforeOrder(restaurantId: string): Promise<{
  allowed: boolean;
  error?: string;
}> {
  try {
    const {
      data,
      error
    } = await supabase.from('restaurant_settings').select('kitchen_paused').eq('restaurant_id', restaurantId).single();
    if (error) {
      console.error(LOG_PREFIX, 'Backend guard fetch error:', error);
      // Allow order if we can't check (fail open)
      return {
        allowed: true
      };
    }
    if (data?.kitchen_paused === true) {
      console.log(LOG_PREFIX, 'Backend guard BLOCKED order creation for restaurant:', restaurantId);
      return {
        allowed: false,
        error: 'La cocina está pausada. No podemos recibir pedidos en este momento. Vuelve en 10 minutos.'
      };
    }
    return {
      allowed: true
    };
  } catch (err) {
    console.error(LOG_PREFIX, 'Backend guard exception:', err);
    return {
      allowed: true
    };
  }
}